import { combineReducers } from 'redux'
import { combineActions, handleActions } from 'redux-actions'

import { newSearch, nextPage } from './actions'
import {
  DEFAULT_HAS_MULTIPLE_PAGES,
  DEFAULT_LIST,
  DEFAULT_LOADING,
  DEFAULT_NEXT_LOADING,
  DEFAULT_NEXT_PARAMS,
  DEFAULT_ORDER_OPTIONS,
  DEFAULT_QUALITY_RESULT_SIZE,
  DEFAULT_RESULT_PARAMS,
  DEFAULT_SEARCH_PARAMS,
  DEFAULT_TOTAL_SIZE
} from './defaultState'

/**
 * does the result list contain multiple pages
 */
const hasMultiplePages = handleActions({
  [newSearch.request]: (state, { payload: { offset } }) => offset > 0,
  [nextPage.success]: () => true
}, DEFAULT_HAS_MULTIPLE_PAGES)

/**
 * Generate reducer that stores a value from search results meta
 * @param {string} propName - The name of the property in results meta
 * @param {any} [defaultValue=null] - Default value - on new search and initially
 * @returns {function(*=, *=): *}
 */
const resultProp = (propName, defaultValue = null) => handleActions({
  [newSearch.request]: () => defaultValue,
  [combineActions(newSearch.success, nextPage.success)]: (
    // eslint-disable-next-line default-param-last
    prevState = defaultValue,
    { meta: { [propName]: value = prevState } }
  ) => value
}, defaultValue)

/**
 * high quality results size
 */
const highQualitySize = resultProp('resultSizeHighQuality', DEFAULT_QUALITY_RESULT_SIZE)

/**
 * current results, cleared on new search, null while waiting for new results
 */
const list = handleActions({
  [newSearch.request]: () => null,
  [newSearch.success]: (_, { payload }) => payload,
  [nextPage.success]: (state, { payload }) => [...state, ...payload],
  [newSearch.failure]: () => []
}, DEFAULT_LIST)

/**
 * keeps track of loading state
 */
const loading = handleActions({
  [newSearch.request]: () => true,
  // skip canceled to prevent flicker of the interface - cancel is followed by new request
  [newSearch.fulfill]: (state, { meta: { canceled } }) => canceled ? state : false
}, DEFAULT_LOADING)

/**
 * low quality results size
 */
const lowQualitySize = resultProp('resultSizeLowQuality', DEFAULT_QUALITY_RESULT_SIZE)

/**
 * keeps track of next page loading state
 */
const nextLoading = handleActions({
  [nextPage.request]: () => true,
  [nextPage.fulfill]: () => false
}, DEFAULT_NEXT_LOADING)

/**
 * search parameters for next page request
 */
const nextParams = resultProp('nextParams', DEFAULT_NEXT_PARAMS)

/**
 * available order options
 */
const orderOptions = resultProp('orderOptions', DEFAULT_ORDER_OPTIONS)

/**
 * search params used to load the current results
 */
const searchParams = handleActions({
  [newSearch.request]: (_, { payload }) => payload
}, DEFAULT_SEARCH_PARAMS)

/**
 * total results size
 */
const totalSize = resultProp('resultSize', DEFAULT_TOTAL_SIZE)
const resultParams = resultProp('resultParams', DEFAULT_RESULT_PARAMS)

export default combineReducers({
  hasMultiplePages,
  highQualitySize,
  list,
  loading,
  lowQualitySize,
  nextLoading,
  nextParams,
  orderOptions,
  searchParams,
  totalSize,
  resultParams
})
